import { CloneRepeatType } from '@shared/enums/CloneRepeatType';
import { EventPublishType } from '@shared/enums/EventPublishType';
import { ExcelExistingEmailAddressType } from '@shared/enums/ExcelExistingEmailAddressType';
import { SortedPage } from '@shared/models/sortedPage';
import { ExternalIdentifier } from '@shared/services/api/legacy/hubspotlists-service/hubspotlist-model';
import { IContactCustomField } from '@shared/services/api/legacy/imports-service/imports-model';
import { IContactColumn } from './invitationlist-model';
import { ITranslation } from '@shared/models/language.model';
import { EventPageType } from '@shared/enums/EventPageType';

export interface IPublishEvent {
    id: string;
}

export class PublishEvent implements IPublishEvent {
    constructor(
        public id: string
    ) { }
}

export interface IStartEvent {
    id: string;
}

export class StartEvent implements IStartEvent {
    constructor(
        public id: string
    ) { }
}

export interface IStopEvent {
    id: string;
}

export class StopEvent implements IStopEvent {
    constructor(
        public id: string
    ) { }
}

export interface ICloneEvent {
    eventId: string;
    name: string;
    fromDate: any;
    toDate: any;
    fromDateUtc: string;
    toDateUtc: string;
    fromDateLocal: string;
    toDateLocal: string;
    cloneTimeline: boolean;
    cloneAdHocMails: boolean;
    cloneAllocations: boolean;
    cloneInvitations: boolean;
    cloneTicketdesigns: boolean;
    cloneLocationDateTimes: boolean;
    repeatEvent: ICloneRepeat;
    cloneToTenants: ICloneTenants;
    publishEvent: boolean;
    cloneAdvancedTemplate: boolean;
}

export class CloneEvent implements ICloneEvent {
    constructor(
        public eventId: string,
        public name: string,
        public fromDate: any,
        public toDate: any,
        public fromDateUtc: string,
        public toDateUtc: string,
        public fromDateLocal: string,
        public toDateLocal: string,
        public cloneTimeline: boolean,
        public cloneAdHocMails: boolean,
        public cloneAllocations: boolean,
        public cloneInvitations: boolean,
        public cloneTicketdesigns: boolean,
        public cloneLocationDateTimes: boolean,
        public repeatEvent: ICloneRepeat,
        public cloneToTenants: ICloneTenants,
        public publishEvent: boolean,
        public cloneAdvancedTemplate: boolean
    ) { }
}

export class EventsPage extends SortedPage {
    // The events owner filter
    public ownerId: string = null;
    // The events filter dropdown
    public eventsFilter = 0;
    // The events search value
    public searchValue = '';
    // The event sort filter
    public eventsSortFilter = 0;
    // The event tags filter
    public tagIds = [];
}

export class EventsPagedData<T> {
    public data = new Array<T>();
    public page = new EventsPage();
}

export interface IEditEventTranslation {
    id: string;
    eventTranslations: IEventUpdateTranslation[];
}

export class EditEventTranslation implements IEditEventTranslation {
    constructor(
        public id: string,
        public eventTranslations: IEventUpdateTranslation[]
    ) { }
}

export interface IEventUpdateTranslation {
    languageId: string;
    isDefault: boolean;
    eventName: string;
    locationDateTimeTranslations: ILocationDateTimeTranslation[];
}

export class EventUpdateTranslation implements IEventUpdateTranslation {
    constructor(
        public languageId: string,
        public isDefault: boolean,
        public eventName: string,
        public locationDateTimeTranslations: ILocationDateTimeTranslation[]
    ) { }
}

export interface IEventUpdateLanguages {
    id: string;
    languageId: string;
    isDefault: boolean;
}

export class EventUpdateLanguages implements IEventUpdateLanguages {
    constructor(
        public id: string,
        public languageId: string,
        public isDefault: boolean
    ) { }
}

export interface ILocationDateTimeTranslation {
    id: string;
    locationDateTimeName: string;
}

export class LocationDateTimeTranslation implements ILocationDateTimeTranslation {
    constructor(
        public id: string,
        public locationDateTimeName: string
    ) { }
}

export interface IDefaultLanguage {
    languageId: string;
}

export interface IFieldTranslations {
    id: string;
    value: string;
    isDefault: boolean;
    languageId: string;
}

export interface IContactTag {
    id: string;
    translations: IFieldTranslations[];
}
export class ContactTag implements IContactTag {
    constructor(
        public id: string,
        public translations: IFieldTranslations[]
    ) { }
}

export interface ITag {
    value: string;
    display: string;
}

export class Tag implements ITag {
    constructor(
        public value: string,
        public display: string
    ) { }
}

export interface IEditEventPublicPages {
    id: string;
    publicPages: IPublicPageForEvent[];
}

export class EditEventPublicPages implements IEditEventPublicPages {
    constructor(
        public id: string,
        public publicPages: IPublicPageForEvent[]
    ) { }
}

export interface IPublicPageForEvent {
    id: string;
    allowPreRegistration: boolean;
    eventPublishType: EventPublishType;
    infoButtonPageType: EventPageType;
}

export class PublicPageForEvent implements IPublicPageForEvent {
    constructor(
        public id: string,
        public allowPreRegistration: boolean,
        public eventPublishType: EventPublishType,
        public infoButtonPageType: EventPageType
    ) { }
}

export interface IPrepareExcelInvitationsRequest {
    accountOwnerId: string;
    languageId: string;
    ticketHolderId: string;
    contactType: string;
    groupIds: string[];
    eventId: string;
    data: any[][];
    contactColumns: IContactColumn[];
    permissionSettings: IPermissionSetting[];
}

export class PrepareExcelInvitationsRequest implements IPrepareExcelInvitationsRequest {
    constructor(
        public accountOwnerId: string,
        public languageId: string,
        public ticketHolderId: string,
        public contactType: string,
        public eventId: string,
        public groupIds: string[],
        public data: any[][],
        public contactColumns: IContactColumn[],
        public permissionSettings: IPermissionSetting[]
    ) { }
}

export interface IPrepareDatabaseInvitationsRequest {
    contacts: IImportData[];
    doublesEmailAddressExcel: IImportData[];
    excelExistingEmailAddressType: ExcelExistingEmailAddressType;
    canUpdateLanguageId: boolean;
    canUpdateAccountOwnerId: boolean;
    canUpdateTicketHolderId: boolean;
    canUpdateGroups: boolean;
    permissionSettings: IPermissionSetting[];
}

export class PrepareDatabaseInvitationsRequest implements IPrepareDatabaseInvitationsRequest {
    constructor(
        public contacts: IImportData[],
        public doublesEmailAddressExcel: IImportData[],
        public excelExistingEmailAddressType: ExcelExistingEmailAddressType,
        public canUpdateLanguageId: boolean,
        public canUpdateAccountOwnerId: boolean,
        public canUpdateTicketHolderId: boolean,
        public canUpdateGroups: boolean,
        public permissionSettings: IPermissionSetting[]
    ) { }
}

export interface IPrepareExcelHubspotListsRequest {
    accountOwnerId: string;
    languageId: string;
    ticketHolderId: string;
    contactType: string;
    data: any[][];
    contactColumns: IContactColumn[];
}


export class PrepareExcelHubspotListsRequest implements IPrepareExcelHubspotListsRequest {
    constructor(
        public accountOwnerId: string,
        public languageId: string,
        public ticketHolderId: string,
        public contactType: string,
        public data: any[][],
        public contactColumns: IContactColumn[]
    ) { }
}

export interface IPrepareDatabaseContactListsRequest {
    contacts: IImportData[];
    doublesEmailAddressExcel: IImportData[];
    excelExistingEmailAddressType: ExcelExistingEmailAddressType;
    canUpdateLanguageId: boolean;
    canUpdateAccountOwnerId: boolean;
    canUpdateTicketHolderId: boolean;
}

export class PrepareDatabaseContactListsRequest implements IPrepareDatabaseContactListsRequest {
    constructor(
        public contacts: IImportData[],
        public doublesEmailAddressExcel: IImportData[],
        public excelExistingEmailAddressType: ExcelExistingEmailAddressType,
        public canUpdateLanguageId: boolean,
        public canUpdateAccountOwnerId: boolean,
        public canUpdateTicketHolderId: boolean
    ) { }
}

export interface IPermissionSetting {
    id: string;
    name: string;
    defaultValue: boolean;
    canUpdate: boolean;
}

export class PermissionSetting implements IPermissionSetting {
    constructor(
        public id: string,
        public name: string,
        public defaultValue: boolean,
        public canUpdate: boolean
    ) { }
}

export interface IImportData {
    contact: IImportContact;
    errorMessage: string;
    row: number;
}

export interface IImportContact {
    firstName: string;
    lastName: string;
    genderId: string;
    jobTitle: string;
    type: number;
    languageId: string;
    email: string;
    phone: string;
    mobile: string;
    companyName: string;
    companyId: string;
    accountOwnerId: string;
    crmId: string;
    customerNumber: string;
    addressLineOne: string;
    addressLineTwo: string;
    city: string;
    postalCode: string;
    country: string;
    salutation: string;
    ticketHolderId: string;
    ticketHolderName: string;
    ticketHolderEmail: string;
    accessCode: string;
    smsPermission: boolean;
    externalIdentifier: ExternalIdentifier;
    contactCustomFields: IContactCustomField[];
}

export class ImportContact implements IImportContact
{
    firstName: string;
    lastName: string;
    genderId: string;
    jobTitle: string;
    type: number;
    languageId: string;
    email: string;
    phone: string;
    mobile: string;
    companyName: string;
    companyId: string;
    accountOwnerId: string;
    crmId: string;
    customerNumber: string;
    addressLineOne: string;
    addressLineTwo: string;
    city: string;
    postalCode: string;
    country: string;
    salutation: string;
    ticketHolderId: string;
    ticketHolderName: string;
    ticketHolderEmail: string;
    accessCode: string;
    smsPermission: boolean;
    externalIdentifier: ExternalIdentifier;
    contactCustomFields: IContactCustomField[];
}

export class ImportData implements IImportData {
    contact: IImportContact;
    errorMessage: string;
    row: number;
}

export interface ICloneRepeat {
    repeatEvent: boolean;
    repeatType: CloneRepeatType;
    repeatAmount: number;
}

export class CloneRepeat implements ICloneRepeat {
    constructor(
        public repeatEvent: boolean,
        public repeatType: CloneRepeatType,
        public repeatAmount: number
    ) { }
}

export interface ICloneTenants {
    cloneToTenants: boolean;
    tenants: string[];
}

export class CloneTenants implements ICloneTenants {
    constructor(
        public cloneToTenants: boolean,
        public tenants: string[]
    ) { }
}

export interface EditCalendarTranslations {
    translations: ITranslation[];
    venueId: string;
    venueName: string;
}

export interface CalendarTranslationsResponse {
    calendarTranslations: ITranslation[];
}
